/**
 * Cliente HTTP autenticado — inyecta JWT, X-Tenant-Id e Idempotency-Key.
 *
 * Gestiona el refresh automático del access token (single-flight)
 * y redirige a /login si la sesión expiró de forma definitiva.
 *
 * @taskId T649
 * @see functional/Cycles/cycle-5/tasks.md HU-25-FE
 */
import { authStore } from '../store/auth-store';
import { authApi, AuthApiError } from '../api/auth/auth.api';
import { IDEMPOTENCY_HEADER, IDEMPOTENCY_METHODS } from '../idempotency/config';

export interface AuthFetchOptions extends RequestInit {
  skipAuthRefresh?: boolean;
  idempotencyKey?: string;
  /**
   * No limpiar la sesión ni redirigir a `/login` si el refresh falla o no hay
   * refresh token — devuelve la respuesta 401 tal cual para que el caller la
   * maneje. Pensado para consumidores sin sesión interactiva real (p. ej. la
   * Wallet demo, que opera con un token de operador silencioso): sacarlos a
   * `/login` los saca de su propio flujo sin que tengan cómo volver a loguearse
   * ahí. Default `false` — no cambia el comportamiento existente.
   */
  skipSessionExpiredRedirect?: boolean;
}

type RefreshOutcome =
  | { ok: true }
  | { ok: false; reason: 'no-token' | 'invalid-refresh' | 'transient' };

// Promesa compartida para coalescer N requests en 1 sola llamada a /auth/refresh.
let inflightRefresh: Promise<RefreshOutcome> | null = null;

async function doRefresh(): Promise<RefreshOutcome> {
  const snap = authStore.getState();
  const { refreshToken, role, tenantId } = snap;

  if (!refreshToken) return { ok: false, reason: 'no-token' };
  if (!role || !tenantId) return { ok: false, reason: 'invalid-refresh' };

  try {
    const res = await authApi.refresh({ refreshToken });
    // Re-check post-await: si el usuario hizo logout o cambió la sesión durante
    // el round-trip, NO resucitar la sesión con los tokens nuevos.
    const post = authStore.getState();
    if (!post.refreshToken || post.refreshToken !== refreshToken) {
      return { ok: false, reason: 'invalid-refresh' };
    }
    authStore.getState().setSession({
      accessToken: res.accessToken,
      refreshToken: res.refreshToken,
      expiresAt: res.expiresAt,
      refreshExpiresAt: res.refreshExpiresAt,
      role,
      tenantId,
    });
    return { ok: true };
  } catch (err) {
    if (err instanceof AuthApiError && err.appError.httpStatus === 401) {
      return { ok: false, reason: 'invalid-refresh' };
    }
    return { ok: false, reason: 'transient' };
  }
}

async function ensureRefreshed(): Promise<RefreshOutcome> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = doRefresh().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

function triggerSessionExpired(): void {
  authStore.getState().clearAuth();
  if (typeof window === 'undefined' || !window.location) return;
  const path = window.location.pathname;
  // Evitar bucle: si ya estamos en cualquier ruta de auth, no redirigir.
  if (path === '/login' || path.startsWith('/login/') || path.startsWith('/auth/')) return;
  window.location.assign('/login?reason=session-expired');
}

function isIdempotencyMethod(method: string | undefined): boolean {
  if (!method) return false;
  return IDEMPOTENCY_METHODS.includes(method.toUpperCase());
}

function buildHeaders(init: AuthFetchOptions | undefined): Headers {
  const headers = new Headers(init?.headers);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  const { token, tenantId } = authStore.getState();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  } else {
    headers.delete('Authorization');
  }
  // RULE-28: X-Tenant-Id requerido en todas las requests al backend.
  // El backend lo usa para aislar datos multi-tenant (P-14/P-15).
  if (tenantId && !headers.has('X-Tenant-Id')) {
    headers.set('X-Tenant-Id', tenantId);
  }
  if (init?.idempotencyKey && isIdempotencyMethod(init.method) && !headers.has(IDEMPOTENCY_HEADER)) {
    headers.set(IDEMPOTENCY_HEADER, init.idempotencyKey);
  }
  return headers;
}

export async function authFetch(
  input: RequestInfo,
  init?: AuthFetchOptions,
): Promise<Response> {
  const { skipAuthRefresh, skipSessionExpiredRedirect, idempotencyKey: _idempotencyKey, ...rest } = init ?? {};
  const headers = buildHeaders(init);
  const response = await fetch(input, { ...rest, headers });

  if (response.status !== 401 || skipAuthRefresh === true) {
    return response;
  }

  const hasRefreshToken = !!authStore.getState().refreshToken;
  if (!hasRefreshToken) {
    if (!skipSessionExpiredRedirect) triggerSessionExpired();
    return response;
  }

  const outcome = await ensureRefreshed();
  if (outcome.ok) {
    const retryHeaders = buildHeaders(init);
    return fetch(input, { ...rest, headers: retryHeaders });
  }

  if (!skipSessionExpiredRedirect && (outcome.reason === 'invalid-refresh' || outcome.reason === 'no-token')) {
    triggerSessionExpired();
  }
  return response;
}
